// ABOUTME: Enterprise-grade slide-out data editor with live chart preview.
// ABOUTME: Supports single and multi-series chart data editing with ChartDataEditor grid.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, AlertCircle, Loader2, BarChart3, LineChart, PieChart, AreaChart, Save, Layers } from 'lucide-react';
import { ReportChart } from './ReportChart';
import { ChartDataEditor } from './ChartDataEditor';
import { validateChartData, detectChartStructure, type ChartType, type ValidationError } from '@/utils/chartValidation';
import { apiClient } from '@/services/api';

interface DataEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  sectionId: string;
  initialData: Record<string, unknown>[];
  initialChartType: ChartType;
  initialTitle?: string;
  initialCategoryKey?: string;
  version: number;
  onSaveSuccess: (newVersion: number, data: Record<string, unknown>[], chartType: ChartType) => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const SINGLE_SERIES_CHART_TYPE_OPTIONS: { type: ChartType; label: string; icon: React.ReactNode }[] = [
  { type: 'bar', label: 'Bar', icon: <BarChart3 className="w-4 h-4" /> },
  { type: 'line', label: 'Line', icon: <LineChart className="w-4 h-4" /> },
  { type: 'area', label: 'Area', icon: <AreaChart className="w-4 h-4" /> },
  { type: 'pie', label: 'Pie', icon: <PieChart className="w-4 h-4" /> },
];

const MULTI_SERIES_CHART_TYPE_OPTIONS: { type: ChartType; label: string; icon: React.ReactNode }[] = [
  { type: 'bar', label: 'Bar', icon: <BarChart3 className="w-4 h-4" /> },
  { type: 'grouped-bar', label: 'Grouped', icon: <Layers className="w-4 h-4" /> },
  { type: 'stacked-bar', label: 'Stacked', icon: <Layers className="w-4 h-4" /> },
  { type: 'line', label: 'Line', icon: <LineChart className="w-4 h-4" /> },
  { type: 'area', label: 'Area', icon: <AreaChart className="w-4 h-4" /> },
];

export function DataEditorModal({
  isOpen,
  onClose,
  reportId,
  sectionId,
  initialData,
  initialChartType,
  initialTitle,
  initialCategoryKey,
  version,
  onSaveSuccess,
}: DataEditorModalProps) {
  const [data, setData] = useState<Record<string, unknown>[]>(initialData);
  const [categoryKey, setCategoryKey] = useState<string>(() => {
    const structure = detectChartStructure(initialData);
    return initialCategoryKey || structure.categoryKey || 'label';
  });
  const [seriesKeys, setSeriesKeys] = useState<string[]>(() => {
    const structure = detectChartStructure(initialData);
    return structure.seriesKeys.length > 0 ? structure.seriesKeys : ['value'];
  });
  const [chartType, setChartType] = useState<ChartType>(initialChartType);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const currentVersion = useRef(version);
  const panelRef = useRef<HTMLDivElement>(null);

  const isMultiSeries = seriesKeys.length > 1;

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      setData(initialData);
      const structure = detectChartStructure(initialData);
      setCategoryKey(initialCategoryKey || structure.categoryKey || 'label');
      setSeriesKeys(structure.seriesKeys.length > 0 ? structure.seriesKeys : ['value']);
      setChartType(initialChartType);
      currentVersion.current = version;
      setSaveStatus('idle');
      setHasUnsavedChanges(false);
    }
  }, [isOpen, initialData, initialChartType, version, initialCategoryKey]);

  // Validate on data change
  useEffect(() => {
    const errors = validateChartData(data, chartType);
    setValidationErrors(errors);
  }, [data, chartType]);

  // Chart type options depend on single vs multi-series
  const chartTypeOptions = useMemo(() => {
    return isMultiSeries ? MULTI_SERIES_CHART_TYPE_OPTIONS : SINGLE_SERIES_CHART_TYPE_OPTIONS;
  }, [isMultiSeries]);

  // Normalize the display chart type (stacked-bar/grouped-bar -> bar for ReportChart)
  const displayChartType = chartType === 'stacked-bar' || chartType === 'grouped-bar' ? 'bar' : chartType;

  // Save function
  const handleSave = useCallback(async () => {
    if (validationErrors.length > 0) return;

    setSaveStatus('saving');
    try {
      const response = await apiClient.reports.updateVisualization(
        reportId,
        sectionId,
        {
          data: data,
          config: { type: chartType },
          chart_type: chartType,
        },
        { ifMatch: currentVersion.current }
      );

      currentVersion.current = response.version;
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      onSaveSuccess(response.version, data, chartType);

      // Close after brief delay to show "Saved" status
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
    }
  }, [data, chartType, reportId, sectionId, validationErrors.length, onSaveSuccess, onClose]);

  const handleChartTypeChange = useCallback((newType: ChartType) => {
    setChartType(newType);
    setHasUnsavedChanges(true);
  }, []);

  // Handle data changes from ChartDataEditor
  const handleDataChange = useCallback((newData: Record<string, unknown>[], newCategoryKey: string, newSeriesKeys: string[]) => {
    setData(newData);
    setCategoryKey(newCategoryKey);
    setSeriesKeys(newSeriesKeys);
    setHasUnsavedChanges(true);
  }, []);

  // Keyboard shortcut for save
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleSave]);

  // Handle close with unsaved changes warning
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9999] bg-black/40"
            onClick={handleClose}
          />

          {/* Slide-out Panel */}
          <motion.div
            ref={panelRef}
            initial={{ x: '100%', opacity: 0.8 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.8 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className="fixed right-0 top-0 bottom-0 z-[10000] w-[620px] max-w-[95vw] bg-surface-0 border-l border-surface-200 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200 bg-surface-50">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-surface-800">
                  Edit Chart Data
                </h2>
                {initialTitle && (
                  <span className="text-xs text-surface-500 px-2 py-1 bg-surface-100 rounded-sm max-w-[140px] truncate">
                    {initialTitle}
                  </span>
                )}
              </div>

              <button
                onClick={handleClose}
                className="p-1.5 rounded-sm text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chart Type Selector */}
            <div className="px-5 py-3 border-b border-surface-200 bg-surface-50">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-surface-500 mr-2">Type:</span>
                {chartTypeOptions.map(option => (
                  <button
                    key={option.type}
                    onClick={() => handleChartTypeChange(option.type)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all cursor-pointer ${
                      chartType === option.type
                        ? 'bg-primary-600 text-white'
                        : 'text-surface-500 hover:text-surface-800 hover:bg-surface-100'
                    }`}
                  >
                    {option.icon}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Live Chart Preview */}
            <div className="h-[280px] px-5 py-4 border-b border-surface-200 bg-surface-50 shrink-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-2">
                Preview
              </div>
              <div className="h-[calc(100%-20px)] bg-surface-0 rounded-sm p-3 border border-surface-200">
                <ReportChart
                  data={isMultiSeries ? undefined : data as { label: string; value: number; prev?: number }[]}
                  rawData={isMultiSeries ? data : undefined}
                  type={displayChartType}
                  height={220}
                  showLegend={isMultiSeries}
                />
              </div>
            </div>

            {/* Data Editor Grid */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="px-5 py-2 border-b border-surface-200 bg-surface-50 flex items-center justify-between shrink-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                  Data ({data.length} rows, {seriesKeys.length} series)
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                <ChartDataEditor
                  data={data}
                  categoryKey={categoryKey}
                  seriesKeys={seriesKeys}
                  validationErrors={validationErrors}
                  onChange={handleDataChange}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 flex items-center justify-between border-t border-surface-200 bg-surface-50">
              <div className="flex items-center gap-3">
                {/* Save Status */}
                <AnimatePresence mode="wait">
                  {saveStatus === 'saved' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center gap-1.5 text-xs text-success"
                    >
                      <Check className="w-4 h-4" />
                      <span>Saved</span>
                    </motion.div>
                  )}
                  {saveStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center gap-1.5 text-xs text-error"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>Save failed</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {hasUnsavedChanges && saveStatus === 'idle' && (
                  <span className="text-xs text-accent-600">
                    Unsaved changes
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="h-9 px-4 rounded-sm text-sm font-medium text-surface-600 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={validationErrors.length > 0 || saveStatus === 'saving'}
                  className={`h-9 px-5 rounded-sm text-sm font-medium flex items-center gap-2 transition-all cursor-pointer ${
                    validationErrors.length > 0
                      ? 'bg-surface-100 text-surface-400 cursor-not-allowed'
                      : hasUnsavedChanges
                        ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg shadow-primary-600/20'
                        : 'bg-surface-100 text-surface-800 hover:bg-surface-200'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          {/* Discard Changes Confirmation */}
          <AnimatePresence>
            {showDiscardConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[10001] flex items-center justify-center"
              >
                <div className="absolute inset-0 bg-black/60" onClick={cancelDiscard} />
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="relative bg-surface-0 border border-surface-200 rounded-sm shadow-modal p-5 max-w-sm mx-4"
                >
                  <h3 className="text-base font-semibold text-surface-800 mb-2">
                    Discard changes?
                  </h3>
                  <p className="text-sm text-surface-600 mb-5">
                    You have unsaved changes that will be lost if you close this panel.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={cancelDiscard}
                      className="h-9 px-4 rounded-sm text-sm font-medium text-surface-600 hover:text-surface-800 hover:bg-surface-100 transition-colors cursor-pointer"
                    >
                      Keep Editing
                    </button>
                    <button
                      onClick={confirmDiscard}
                      className="h-9 px-4 rounded-sm text-sm font-medium bg-error text-white hover:bg-error/90 transition-colors cursor-pointer"
                    >
                      Discard
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
